import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/utils/supabase/client';
import { createLogger } from '@/utils/logger/devLogger';
import { useDebounce } from '@/hooks/state';

const employeeIdLogger = createLogger('EmployeeIdValidation');

export interface EmployeeIdValidationState {
  isChecking: boolean;
  isAvailable: boolean | null;
  isDuplicate: boolean;
  error: string | null;
  message: string | null;
}

export interface UseEmployeeIdValidationOptions {
  organizationId: string;
  currentUserId: string; // Auth user ID to exclude from duplicate check
  currentEmployeeId?: string; // Current employee ID to allow re-entry
  debounceMs?: number;
}

/**
 * Custom hook for real-time Employee ID validation
 * 
 * Features:
 * - Organization-isolated duplicate checking
 * - Excludes current user from duplicate detection
 * - Allows re-entry of user's current employee ID
 * - Debounced API calls to prevent excessive requests
 * - Comprehensive validation states
 */
export function useEmployeeIdValidation(
  employeeId: string,
  options: UseEmployeeIdValidationOptions
): EmployeeIdValidationState {
  const {
    organizationId,
    currentUserId,
    currentEmployeeId,
    debounceMs = 500
  } = options;

  const [validationState, setValidationState] = useState<EmployeeIdValidationState>({
    isChecking: false,
    isAvailable: null,
    isDuplicate: false,
    error: null,
    message: null
  });

  // Debounce the employee ID to avoid excessive API calls
  const debouncedEmployeeId = useDebounce(employeeId, debounceMs);

  const validateEmployeeId = useCallback(async (idToCheck: string) => {
    // Reset state and start checking
    setValidationState({
      isChecking: true,
      isAvailable: null,
      isDuplicate: false,
      error: null,
      message: null
    });

    try {
      // Empty or whitespace-only IDs are valid (optional field)
      const trimmedId = idToCheck.trim();
      if (!trimmedId) {
        setValidationState({
          isChecking: false,
          isAvailable: true,
          isDuplicate: false,
          error: null,
          message: null
        });
        return;
      }

      // If user is re-entering their current employee ID, it's valid (case-insensitive)
      if (currentEmployeeId && trimmedId.toLowerCase() === currentEmployeeId.trim().toLowerCase()) {
        setValidationState({
          isChecking: false,
          isAvailable: true,
          isDuplicate: false,
          error: null,
          message: 'Current employee ID'
        });
        return;
      }

      employeeIdLogger.debug('Checking employee ID availability (case-insensitive)', {
        employeeId: trimmedId,
        organizationId,
        currentUserId,
        currentEmployeeId
      });

      const params = new URLSearchParams({
        organizationId,
        employeeId: trimmedId,
      });
      if (currentUserId) params.set('excludeAuthUserId', currentUserId);

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setValidationState({
          isChecking: false,
          isAvailable: null,
          isDuplicate: false,
          error: 'Your session has expired. Please sign in again.',
          message: null
        });
        return;
      }

      const res = await fetch(`/api/staff/employee-id-check?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        employeeIdLogger.error('Error checking employee ID:', body?.error || res.status);
        setValidationState({
          isChecking: false,
          isAvailable: null,
          isDuplicate: false,
          error: 'Unable to verify employee ID. Please try again.',
          message: null
        });
        return;
      }

      if (body.available === false) {
        setValidationState({
          isChecking: false,
          isAvailable: false,
          isDuplicate: true,
          error: null,
          message: body.message || 'This employee ID is already in use.'
        });
      } else {
        setValidationState({
          isChecking: false,
          isAvailable: true,
          isDuplicate: false,
          error: null,
          message: 'Employee ID is available'
        });
      }

    } catch (error) {
      employeeIdLogger.error('Unexpected error during employee ID validation:', error);
      setValidationState({
        isChecking: false,
        isAvailable: null,
        isDuplicate: false,
        error: 'An unexpected error occurred. Please try again.',
        message: null
      });
    }
  }, [organizationId, currentUserId, currentEmployeeId]);

  // Trigger validation when debounced employee ID changes
  useEffect(() => {
    if (debouncedEmployeeId !== undefined) {
      validateEmployeeId(debouncedEmployeeId);
    }
  }, [debouncedEmployeeId, validateEmployeeId]);

  return validationState;
}

/**
 * Helper function to get appropriate validation styling
 */
export function getEmployeeIdValidationStyle(state: EmployeeIdValidationState): {
  hasError: boolean;
  hasSuccess: boolean;
  isLoading: boolean;
} {
  return {
    hasError: state.isDuplicate || !!state.error,
    hasSuccess: state.isAvailable === true && !state.isDuplicate && !state.error,
    isLoading: state.isChecking
  };
}

/**
 * Helper function to get validation message with appropriate styling
 */
export function getEmployeeIdValidationMessage(state: EmployeeIdValidationState): {
  message: string | null;
  type: 'error' | 'success' | 'info' | null;
} {
  if (state.error) {
    return { message: state.error, type: 'error' };
  }
  
  if (state.isDuplicate) {
    return { message: state.message || 'Employee ID is already taken', type: 'error' };
  }
  
  if (state.isAvailable === true && state.message) {
    return { message: state.message, type: 'success' };
  }
  
  if (state.isChecking) {
    return { message: 'Checking availability...', type: 'info' };
  }
  
  return { message: null, type: null };
} 